import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, Download, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  listClients, createReferralPartner, getFacilityLead, deleteFacilityLead, archiveFacilityLead, restoreFacilityLead,
  binFacilityLead, unbinFacilityLead,
} from "@/lib/api";
import ContractPatientProfile from "@/components/clients/ContractPatientProfile";
import AddContractPatientDialog from "@/components/clients/AddContractPatientDialog";
import { QuickLeadDialog, QuickFacilityDialog } from "@/components/help/QuickActionDialogs";
import { FacilityDetailSheet } from "@/components/FacilityLeadsPanel";
import ClientList, { AddClientMenu } from "@/components/clients/hub/ClientList";
import ClientDetail from "@/components/clients/hub/ClientDetail";
import { FormDialog, BulkStatusDialog } from "@/components/clients/hub/ClientDialogs";
import { ADAPTERS } from "@/components/clients/hub/clientAdapters";
import {
  CATEGORY_TABS, KIND_META, SORTS, STATUS_OPTIONS, statusLabel, rowKey, fmtDate, errMsg,
} from "@/components/clients/hub/clientMeta";

const ALL = "__all";
const inTab = (r, tab) => tab === "all" || (tab === "referrals" ? r.kind === "referral_partner" || !!r.referral_source : r.kind === tab);
const PARTNER_FIELDS = [
  { key: "name", label: "Name", required: true }, { key: "organization", label: "Organization" },
  { key: "partner_type", label: "Partner type", placeholder: "Physician, case manager, clinic…" },
  { key: "phone", label: "Phone", type: "tel" }, { key: "email", label: "Email", type: "email" },
  { key: "address", label: "Address", type: "textarea" },
];

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div className="rounded-xl border bg-white px-3 pt-1.5 pb-0.5 min-w-[150px] flex-1 sm:flex-none" style={{ borderColor: "#E2E8F0" }}>
    <div className="text-[11px] font-medium" style={{ color: "#64748B" }}>{label}</div>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 border-0 px-0 shadow-none focus:ring-0 text-sm font-medium" style={{ color: "#0B2545" }} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const toCsv = (rows) => {
  const cols = [["Name", "name"], ["Type", (r) => KIND_META[r.kind].label], ["Status", (r) => statusLabel(r.kind, r.status)],
    ["Email", "email"], ["Phone", "phone"], ["Location", "location"], ["Address", "address"], ["Referral source", "referral_source"],
    ["Date added", (r) => fmtDate(r.created_at)], ["Last activity", (r) => fmtDate(r.last_activity_at)], ["Client ID", (r) => r.ref || r.id]];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.map(([h]) => esc(h)).join(","), ...rows.map((r) => cols.map(([, k]) => esc(typeof k === "function" ? k(r) : r[k])).join(","))].join("\n");
};

export default function ClientsPanel({ onSessionExpired }) {
  const navigate = useNavigate();
  const detailRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState(ALL);
  const [statusF, setStatusF] = useState(ALL);
  const [locF, setLocF] = useState(ALL);
  const [sort, setSort] = useState("newest");
  const [selectedKey, setSelectedKey] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [addKind, setAddKind] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [activePatientId, setActivePatientId] = useState(null);
  const [facilityRecord, setFacilityRecord] = useState(null);

  const fetchAll = async () => {
    try { setRows((await listClients()).clients || []); }
    catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error("Failed to load clients.");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);

  const counts = useMemo(() => Object.fromEntries(CATEGORY_TABS.map((t) => [t.id, rows.filter((r) => inTab(r, t.id)).length])), [rows]);
  const statusOptions = useMemo(() => [...new Set(rows.map((r) => statusLabel(r.kind, r.status)))].sort(), [rows]);
  const locationOptions = useMemo(() => [...new Set(rows.map((r) => r.location).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const out = rows.filter((r) => inTab(r, tab)
      && (typeF === ALL || r.kind === typeF)
      && (statusF === ALL || statusLabel(r.kind, r.status) === statusF)
      && (locF === ALL || r.location === locF)
      && (!s || [r.name, r.email, r.phone, r.location, r.address, r.secondary, r.id, r.ref, r.contract_number]
        .some((v) => String(v || "").toLowerCase().includes(s))));
    const by = {
      newest: (a, b) => String(b.created_at).localeCompare(String(a.created_at)),
      oldest: (a, b) => String(a.created_at).localeCompare(String(b.created_at)),
      activity: (a, b) => String(b.last_activity_at).localeCompare(String(a.last_activity_at)),
      az: (a, b) => String(a.name || "").localeCompare(String(b.name || "")),
      za: (a, b) => String(b.name || "").localeCompare(String(a.name || "")),
    }[sort];
    return [...out].sort(by);
  }, [rows, tab, q, typeF, statusF, locF, sort]);

  useEffect(() => {
    if (!filtered.length) { setSelectedKey(null); return; }
    if (!selectedKey || !filtered.some((r) => rowKey(r) === selectedKey)) setSelectedKey(rowKey(filtered[0]));
  }, [filtered, selectedKey]);

  const selectedRow = filtered.find((r) => rowKey(r) === selectedKey) || null;
  const hasFilters = q || typeF !== ALL || statusF !== ALL || locF !== ALL;
  const clearFilters = () => { setQ(""); setTypeF(ALL); setStatusF(ALL); setLocF(ALL); };

  const selectRow = (r) => {
    setSelectedKey(rowKey(r));
    if (!inTab(r, tab)) setTab("all");
    if (window.innerWidth < 1024) setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const openFull = async (r) => {
    if (r.kind === "consumer") navigate(`/admin/leads/${r.id}`);
    else if (r.kind === "contract_patient") setActivePatientId(r.id);
    else if (r.kind === "facility") {
      try { setFacilityRecord(await getFacilityLead(r.id)); } catch (e) { toast.error(errMsg(e, "Couldn't open facility.")); }
    }
  };

  const exportCsv = () => {
    const list = checked.size ? rows.filter((r) => checked.has(rowKey(r))) : filtered;
    const url = URL.createObjectURL(new Blob([toCsv(list)], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `tj-clients-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success(`Exported ${list.length} client${list.length === 1 ? "" : "s"}.`);
  };

  const selectedRows = rows.filter((r) => checked.has(rowKey(r)));
  const bulkKinds = [...new Set(selectedRows.map((r) => r.kind))];
  const bulkApply = async (status) => {
    const results = await Promise.allSettled(selectedRows.map((r) => ADAPTERS[r.kind].setStatus(r.id, status)));
    const failed = results.filter((x) => x.status === "rejected").length;
    if (failed) toast.error(`${failed} of ${results.length} couldn't be updated.`);
    else toast.success(`Updated ${results.length} client${results.length === 1 ? "" : "s"}.`);
    setChecked(new Set());
    fetchAll();
  };

  const applyFacilityUpdate = (u) => { if (u) setFacilityRecord(u); fetchAll(); };

  return (
    <div className="space-y-5" data-testid="clients-panel">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: "#0B2545" }}>Clients</h1>
          <p className="mt-2 text-base" style={{ color: "#475569" }}>Manage all clients — consumers, facilities, providers, and contract patients.</p>
        </div>
        <AddClientMenu big onPick={setAddKind} />
      </div>

      <div className="flex flex-col xl:flex-row xl:items-center gap-3 justify-between">
        <div className="flex flex-wrap gap-2" role="tablist" data-testid="clients-category-tabs">
          {CATEGORY_TABS.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} data-testid={`clients-tab-${t.id}`}
                    className={`h-11 px-5 rounded-xl border text-sm font-semibold transition-colors ${tab === t.id ? "bg-[#0B2545] border-[#0B2545] text-white shadow-sm" : "bg-white border-slate-200 text-[#0B2545] hover:border-[#0B2545]/40"}`}>
              {t.label} ({counts[t.id] || 0})
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="w-48">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-11 rounded-xl bg-white font-medium" style={{ color: "#0B2545" }} data-testid="clients-sort">
                <span className="truncate">Sort: {SORTS.find((s) => s.value === sort).label}</span>
              </SelectTrigger>
              <SelectContent className="bg-white">{SORTS.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`clients-sort-${s.value}`}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={exportCsv} className="h-11 rounded-xl bg-white font-semibold" style={{ color: "#0B2545" }} data-testid="clients-export">
            <Download className="h-4 w-4 mr-2" />Export
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 lg:items-stretch">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "#475569" }} />
          <Input className="h-full min-h-[52px] pl-12 rounded-xl bg-white text-base" placeholder="Search name, email, phone, location, or client ID…"
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search" />
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <FilterSelect label="Client Type" value={typeF} onChange={setTypeF} testid="clients-filter-type"
                        options={[{ value: ALL, label: "All Types" }, ...Object.entries(KIND_META).map(([v, m]) => ({ value: v, label: m.label }))]} />
          <FilterSelect label="Status" value={statusF} onChange={setStatusF} testid="clients-filter-status"
                        options={[{ value: ALL, label: "All Statuses" }, ...statusOptions.map((s) => ({ value: s, label: s }))]} />
          <FilterSelect label="Location" value={locF} onChange={setLocF} testid="clients-filter-location"
                        options={[{ value: ALL, label: "All Locations" }, ...locationOptions.map((s) => ({ value: s, label: s }))]} />
          <button type="button" onClick={clearFilters} disabled={!hasFilters} data-testid="clients-clear-filters"
                  className="text-sm underline underline-offset-2 px-2 disabled:opacity-40 disabled:no-underline" style={{ color: "#475569" }}>
            {hasFilters ? <span className="inline-flex items-center gap-1"><X className="h-3.5 w-3.5" />Clear</span> : "Clear"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start" data-testid="clients-two-column">
          <div className="lg:col-span-5 rounded-2xl border bg-white shadow-sm overflow-hidden lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)] max-h-[70vh] lg:max-h-none" style={{ borderColor: "#E2E8F0" }}>
            {filtered.length ? (
              <ClientList rows={filtered} selectedKey={selectedKey} checked={checked} setChecked={setChecked}
                          onSelect={selectRow} onOpenFull={openFull} onExport={exportCsv} onBulkStatus={() => setBulkOpen(true)}
                          addMenu={<AddClientMenu onPick={setAddKind} />} />
            ) : (
              <div className="py-20 px-6 text-center" data-testid="clients-empty">
                <p className="font-semibold" style={{ color: "#0B2545" }}>No clients match.</p>
                <p className="text-sm mt-1" style={{ color: "#64748B" }}>Try another tab or clear your filters.</p>
                {hasFilters ? <Button variant="outline" className="mt-4" onClick={clearFilters}>Clear filters</Button> : null}
              </div>
            )}
          </div>
          <div ref={detailRef} className="lg:col-span-7 rounded-2xl border bg-white shadow-sm min-h-[60vh] flex flex-col scroll-mt-24" style={{ borderColor: "#E2E8F0" }} data-testid="clients-detail">
            {selectedRow ? (
              <ClientDetail row={selectedRow} allRows={rows} onChanged={fetchAll} onOpenFull={() => openFull(selectedRow)}
                            onSelectRow={selectRow} onArchived={(r) => { setRows((prev) => prev.filter((x) => rowKey(x) !== rowKey(r))); setSelectedKey(null); fetchAll(); }} />
            ) : (
              <div className="grid place-items-center flex-1 text-sm" style={{ color: "#64748B" }}>Select a client to see their details.</div>
            )}
          </div>
        </div>
      )}

      <QuickLeadDialog open={addKind === "consumer"} mode="client" onClose={() => setAddKind(null)} onSaved={fetchAll} />
      <QuickFacilityDialog open={addKind === "facility"} onClose={() => setAddKind(null)}
                           onSaved={() => { toast.info("Facility added to the Facilities & Providers pipeline. It shows here once it's signed or active."); fetchAll(); }} />
      <AddContractPatientDialog open={addKind === "contract_patient"} onClose={() => setAddKind(null)} onCreated={() => fetchAll()} />
      <FormDialog open={addKind === "referral_partner"} onClose={() => setAddKind(null)} title="Add referral partner" testid="add-partner-dialog"
                  fields={PARTNER_FIELDS} initial={{}} submitLabel="Add partner" onSave={createReferralPartner}
                  onSaved={async (p) => { await fetchAll(); if (p) { setTab("referrals"); setSelectedKey(`referral_partner:${p.id}`); } }} />
      <BulkStatusDialog open={bulkOpen} onClose={() => setBulkOpen(false)} count={checked.size}
                        options={bulkKinds.length === 1 ? STATUS_OPTIONS[bulkKinds[0]] : null} onSave={bulkApply} />

      <ContractPatientProfile patientId={activePatientId} onClose={() => setActivePatientId(null)}
                              onUpdate={() => fetchAll()} onDelete={() => { setActivePatientId(null); fetchAll(); }} />
      {facilityRecord ? (
        <FacilityDetailSheet
          record={facilityRecord}
          onClose={() => { setFacilityRecord(null); fetchAll(); }}
          onUpdate={applyFacilityUpdate}
          onOpenContractPatient={(pid) => setActivePatientId(pid)}
          onDelete={async (id) => {
            if (!window.confirm("Delete this facility permanently?")) return;
            try { await deleteFacilityLead(id); setFacilityRecord(null); fetchAll(); toast.success("Facility deleted."); }
            catch { toast.error("Failed to delete."); }
          }}
          onArchive={async (id, archived) => {
            try { applyFacilityUpdate(archived ? await archiveFacilityLead(id) : await restoreFacilityLead(id)); toast.success(archived ? "Archived." : "Restored."); }
            catch { toast.error("Failed."); }
          }}
          onBin={async (id, binned) => {
            try { applyFacilityUpdate(binned ? await binFacilityLead(id) : await unbinFacilityLead(id)); toast.success(binned ? "Moved to bin." : "Restored from bin."); }
            catch { toast.error("Failed."); }
          }}
        />
      ) : null}
    </div>
  );
}
